import { useMemo } from "react";

import { useCan, useRealTimeTypedQuery } from "components";
import { typedQueryClient } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { CarDetailsQueryKeys } from "modules/CarDetails/queryKeys";
import { ApiInstance } from "utils/Api";
import { groupAndSortItemsWithHistory } from "utils/itemsWithHistory";

import type { WebSocketMessageListener } from "components";
import type { Snooze } from "models";

const groupSnoozeItemsWithHistory = (snoozedItems: Snooze[]) =>
  groupAndSortItemsWithHistory(snoozedItems).map(snoozeItem => ({ ...snoozeItem, snooze_history: snoozeItem.history }));

export const useSnoozeItemsQuery = (car_id?: number) => {
  const canListSnoozeItems = useCan(ACL_ACTIVITIES.List, ACL_RESOURCES.Snooze);

  const listeners = useMemo(() => {
    if (!car_id) return [];

    const snoozeListeners: WebSocketMessageListener<Snooze>[] = [
      {
        model: "Snooze",
        action: "delete",
        filter: { car_id },
        callback: ({ data: snoozeItem }) => {
          const snoozeItems = typedQueryClient.getQueryData(CarDetailsQueryKeys.snoozeItems);
          if (!snoozeItems?.length) return;

          typedQueryClient.setQueryData(
            CarDetailsQueryKeys.snoozeItems,
            snoozeItems.filter(i => i.question_result_id !== snoozeItem.question_result_id || i.intervention_id !== snoozeItem.intervention_id)
          );
        }
      },
      {
        model: "Snooze",
        action: "upsert",
        filter: { car_id },
        callback: ({ data: snoozeItem }) => {
          let snoozeItems = typedQueryClient.getQueryData(CarDetailsQueryKeys.snoozeItems);
          if (!snoozeItems) snoozeItems = [];

          const snoozeItemIdx = snoozeItems.findIndex(item => item.id === snoozeItem.id);
          if (snoozeItemIdx < 0) {
            snoozeItems = snoozeItems.concat(snoozeItem);
          } else snoozeItems = snoozeItems.with(snoozeItemIdx, { ...snoozeItems[snoozeItemIdx], ...snoozeItem });

          typedQueryClient.setQueryData(CarDetailsQueryKeys.snoozeItems, snoozeItems);
        }
      }
    ];

    return snoozeListeners;
  }, [car_id]);

  return useRealTimeTypedQuery({
    enabled: !!car_id && canListSnoozeItems,
    queryKey: CarDetailsQueryKeys.snoozeItems,
    queryFn: async ({ endpoint, baseUrl }) => (await ApiInstance.post(endpoint, { car_id }, baseUrl)).data.items,
    listeners,
    select: groupSnoozeItemsWithHistory
  });
};
